/**
 * Inventory context — products, units, TIM-E parts, BIM-E parts, operations.
 *
 * Each resource is fetched independently from the API on mount. A save is only
 * persisted to the server AFTER its initial fetch has succeeded — this prevents
 * the historical data-loss scenario where a stale/empty client state was PUT
 * to the destructive "replace all" endpoint and wiped the DB.
 *
 * Local state still updates immediately on every setX so the UI feels snappy;
 * if the server hasn't been reached yet the in-memory change won't outlive a
 * page refresh, but it also won't overwrite real server data.
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { InventoryProduct, InventoryUnit } from '../data/inventory';
import type { TimEInventoryRow } from '../data/timEInventoryList';
import type { BimEInventoryRow } from '../data/bimEInventoryList';
import type { OperationsInventoryRow } from '../data/timEInventoryList';
import { getInventoryProducts, getInventoryUnits } from '../data/inventory';
import { getTimEPartsInventory, getOperationsInventory } from '../data/timEInventoryList';
import { getBimEPartsInventory } from '../data/bimEInventoryList';
import {
  fetchInventoryProducts,
  saveInventoryProducts,
  fetchInventoryUnitsSnapshot,
  saveInventoryUnits,
  fetchTimEParts,
  saveTimEParts,
  fetchBimEParts,
  saveBimEParts,
  fetchOperationsInventory,
  saveOperationsInventory,
} from '../api/inventoryApi';
import type { InventoryUnitRow, TimEPartsRow, BimEPartsRow, OperationsRow } from '../api/inventoryApi';

interface InventoryContextValue {
  products: InventoryProduct[];
  units: InventoryUnit[];
  timEParts: TimEInventoryRow[];
  bimEParts: BimEInventoryRow[];
  operations: OperationsInventoryRow[];
  setProducts: (next: InventoryProduct[]) => void;
  setUnits: (next: InventoryUnit[]) => void;
  setTimEParts: (next: TimEInventoryRow[]) => void;
  setBimEParts: (next: BimEInventoryRow[]) => void;
  setOperations: (next: OperationsInventoryRow[]) => void;
  refreshProducts: () => Promise<void>;
  refreshUnits: () => Promise<void>;
  refreshTimEParts: () => Promise<void>;
  refreshBimEParts: () => Promise<void>;
  refreshOperations: () => Promise<void>;
  loading: boolean;
  /** Per-resource flag: true once the initial fetch from the server succeeded. Saves are gated on these. */
  loaded: {
    products: boolean;
    units: boolean;
    timEParts: boolean;
    bimEParts: boolean;
    operations: boolean;
  };
}

const InventoryContext = createContext<InventoryContextValue | null>(null);

function mapApiProductToProduct(r: {
  id?: string;
  name?: string;
  type?: string;
  sku?: string;
  availability?: number;
  inUse?: number;
  lowStockBuffer?: number | null;
  catalogStatus?: string;
  replacementCostPerItem?: string;
}): InventoryProduct {
  const cs = r.catalogStatus === 'Void' ? 'Void' : 'Active';
  return {
    id: String(r.id ?? ''),
    name: String(r.name ?? ''),
    type: (r.type === 'Robot' || r.type === 'Accessory' || r.type === 'Stock Item' ? r.type : 'Accessory') as
      | 'Robot'
      | 'Accessory'
      | 'Stock Item',
    sku: String(r.sku ?? ''),
    availability: Number(r.availability) || 0,
    inUse: Number(r.inUse) || 0,
    lowStockBuffer: r.lowStockBuffer != null ? Number(r.lowStockBuffer) : null,
    catalogStatus: cs,
    replacementCostPerItem: String(r.replacementCostPerItem ?? '').trim(),
  };
}

function mapApiUnitToUnit(r: InventoryUnitRow): InventoryUnit {
  const toOptionalNumber = (v: unknown): number | undefined => {
    if (v == null) return undefined;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  return {
    id: String(r.id ?? ''),
    productId: String(r.productId ?? ''),
    countryOfOrigin: String(r.countryOfOrigin ?? ''),
    productType: r.productType != null && String(r.productType).trim() !== '' ? String(r.productType) : undefined,
    model: String(r.model ?? ''),
    serialNumber: String(r.serialNumber ?? ''),
    tempSerialNumber:
      r.tempSerialNumber != null && String(r.tempSerialNumber).trim() !== ''
        ? String(r.tempSerialNumber)
        : undefined,
    status: r.status as InventoryUnit['status'],
    condition: (r.condition === 'Storage' ? 'Working' : r.condition) as InventoryUnit['condition'],
    manufacturer: r.manufacturer != null ? String(r.manufacturer) : undefined,
    location: r.location != null ? String(r.location) : undefined,
    business: r.business != null ? String(r.business) : undefined,
    installationDate:
      r.installationDate != null
        ? String(r.installationDate)
        : undefined,
    trashBins: toOptionalNumber(r.trashBins),
    linenBins: toOptionalNumber(r.linenBins),
    rollingBases: toOptionalNumber(r.rollingBases),
    staticBases: toOptionalNumber(r.staticBases),
    stockQuantity:
      r.stockQuantity != null && Number.isFinite(Number(r.stockQuantity)) && Number(r.stockQuantity) > 0
        ? Math.floor(Number(r.stockQuantity))
        : undefined,
    assembled: Boolean(r.assembled),
  };
}

function mapTimERow(r: TimEPartsRow): TimEInventoryRow {
  return {
    product: String(r.product ?? ''),
    available: String(r.available ?? ''),
    fullyAssembled: String(r.fullyAssembled ?? ''),
    notAssembled: String(r.notAssembled ?? ''),
    backOrdered: String(r.backOrdered ?? ''),
    pendingSales: String(r.pendingSales ?? ''),
    replacementCostPerItem: String(r.replacementCostPerItem ?? ''),
    value: String(r.value ?? ''),
    discardedUnits: String(r.discardedUnits ?? ''),
    wasteDollars: String(r.wasteDollars ?? ''),
    lowStockBuffer: r.lowStockBuffer != null ? Number(r.lowStockBuffer) : null,
    status: r.status === 'Void' ? 'Void' : 'Active',
  };
}

function mapBimERow(r: BimEPartsRow): BimEInventoryRow {
  return {
    product: String(r.product ?? ''),
    available: String(r.available ?? ''),
    fullyAssembled: String(r.fullyAssembled ?? ''),
    notAssembled: String(r.notAssembled ?? ''),
    backOrdered: String(r.backOrdered ?? ''),
    pendingSales: String(r.pendingSales ?? ''),
    replacementCostPerItem: String(r.replacementCostPerItem ?? ''),
    value: String(r.value ?? ''),
    discardedUnits: String(r.discardedUnits ?? ''),
    pendingDelivery: String(r.pendingDelivery ?? ''),
    lowStockBuffer: r.lowStockBuffer != null ? Number(r.lowStockBuffer) : null,
    status: r.status === 'Void' ? 'Void' : 'Active',
  };
}

function mapOperationsRow(r: OperationsRow): OperationsInventoryRow {
  return {
    product: String(r.product ?? ''),
    available: String(r.available ?? ''),
    replacementCostPerItem: String(r.replacementCostPerItem ?? ''),
    value: String(r.value ?? ''),
  };
}

export function InventoryProvider({ children }: { children: React.ReactNode }) {
  const [products, setProductsState] = useState<InventoryProduct[]>(() => getInventoryProducts());
  const [units, setUnitsState] = useState<InventoryUnit[]>(() => getInventoryUnits());
  const [timEParts, setTimEPartsState] = useState<TimEInventoryRow[]>(() => getTimEPartsInventory());
  const [bimEParts, setBimEPartsState] = useState<BimEInventoryRow[]>(() => getBimEPartsInventory());
  const [operations, setOperationsState] = useState<OperationsInventoryRow[]>(() => getOperationsInventory());
  const [loading, setLoading] = useState(true);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [timEPartsLoaded, setTimEPartsLoaded] = useState(false);
  const [bimEPartsLoaded, setBimEPartsLoaded] = useState(false);
  const [operationsLoaded, setOperationsLoaded] = useState(false);

  /**
   * Revision of the units list this tab currently holds, refreshed on every fetch and
   * every successful save. Sent with each save so the server can refuse a list that was
   * loaded before someone else's changes instead of silently deleting their rows.
   */
  const unitsRevisionRef = useRef<string | null>(null);
  /**
   * Unit saves run one at a time. Each save is based on the revision the previous one
   * returned, so a quick sequence of edits (deleting rows one after another) doesn't
   * trip the server's staleness check on its own in-flight write.
   */
  const unitsSaveChainRef = useRef<Promise<void>>(Promise.resolve());

  /** Mirror the loaded flags in refs so setX callbacks can read the current value without resubscribing. */
  const loadedRef = useRef({ products: false, units: false, timEParts: false, bimEParts: false, operations: false });
  loadedRef.current = {
    products: productsLoaded,
    units: unitsLoaded,
    timEParts: timEPartsLoaded,
    bimEParts: bimEPartsLoaded,
    operations: operationsLoaded,
  };

  const refreshProducts = useCallback(async () => {
    try {
      const data = await fetchInventoryProducts();
      setProductsState(data.map(mapApiProductToProduct));
      setProductsLoaded(true);
    } catch (err) {
      console.warn('[InventoryContext] Failed to refresh products:', err);
    }
  }, []);

  const refreshUnits = useCallback(async () => {
    try {
      const data = await fetchInventoryUnitsSnapshot();
      unitsRevisionRef.current = data.revision;
      setUnitsState(data.units.map(mapApiUnitToUnit));
      setUnitsLoaded(true);
    } catch (err) {
      console.warn('[InventoryContext] Failed to refresh units:', err);
    }
  }, []);

  const refreshTimEParts = useCallback(async () => {
    try {
      const data = await fetchTimEParts();
      setTimEPartsState(data.map(mapTimERow));
      setTimEPartsLoaded(true);
    } catch (err) {
      console.warn('[InventoryContext] Failed to refresh TIM-E parts:', err);
    }
  }, []);

  const refreshBimEParts = useCallback(async () => {
    try {
      const data = await fetchBimEParts();
      setBimEPartsState(data.map(mapBimERow));
      setBimEPartsLoaded(true);
    } catch (err) {
      console.warn('[InventoryContext] Failed to refresh BIM-E parts:', err);
    }
  }, []);

  const refreshOperations = useCallback(async () => {
    try {
      const data = await fetchOperationsInventory();
      setOperationsState(data.map(mapOperationsRow));
      setOperationsLoaded(true);
    } catch (err) {
      console.warn('[InventoryContext] Failed to refresh operations:', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      /** allSettled so one failed fetch doesn't poison the others — a failed resource just stays "not loaded". */
      const [p, u, t, b, o] = await Promise.allSettled([
        fetchInventoryProducts(),
        fetchInventoryUnitsSnapshot(),
        fetchTimEParts(),
        fetchBimEParts(),
        fetchOperationsInventory(),
      ]);
      if (cancelled) return;
      if (p.status === 'fulfilled') {
        setProductsState(p.value.map(mapApiProductToProduct));
        setProductsLoaded(true);
      } else {
        console.warn('[InventoryContext] Initial fetchInventoryProducts failed:', p.reason);
      }
      if (u.status === 'fulfilled') {
        unitsRevisionRef.current = u.value.revision;
        setUnitsState(u.value.units.map(mapApiUnitToUnit));
        setUnitsLoaded(true);
      } else {
        console.warn('[InventoryContext] Initial fetchInventoryUnits failed:', u.reason);
      }
      if (t.status === 'fulfilled') {
        setTimEPartsState(t.value.map(mapTimERow));
        setTimEPartsLoaded(true);
      } else {
        console.warn('[InventoryContext] Initial fetchTimEParts failed:', t.reason);
      }
      if (b.status === 'fulfilled') {
        setBimEPartsState(b.value.map(mapBimERow));
        setBimEPartsLoaded(true);
      } else {
        console.warn('[InventoryContext] Initial fetchBimEParts failed:', b.reason);
      }
      if (o.status === 'fulfilled') {
        setOperationsState(o.value.map(mapOperationsRow));
        setOperationsLoaded(true);
      } else {
        console.warn('[InventoryContext] Initial fetchOperationsInventory failed:', o.reason);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * Each setX updates local state immediately for UI responsiveness, then
   * persists to the server only if the initial fetch for that resource has
   * succeeded. This blocks the data-loss path where a stale/empty client
   * state would otherwise be PUT to the destructive "replace all" endpoint.
   */
  /**
   * On save failure, surface the server's error message and roll the local state back to
   * what the server actually has. This ensures (a) the user sees the friendly guard message
   * (e.g. "refusing to delete product X — still has 37 units attached") and (b) the UI
   * doesn't continue showing optimistic data that the DB rejected.
   */
  const handleSaveFailure = useCallback(
    async (
      label: string,
      err: unknown,
      refresh: () => Promise<void>
    ): Promise<void> => {
      console.error(`[InventoryContext] Failed to save ${label}:`, err);
      const message = err instanceof Error ? err.message : `Failed to save ${label}.`;
      try {
        if (typeof window !== 'undefined' && typeof window.alert === 'function') {
          window.alert(message);
        }
      } catch {
        /* ignore environments without window.alert */
      }
      try {
        await refresh();
      } catch {
        /* refresh errors are already logged inside refreshX */
      }
    },
    []
  );

  const setProducts = useCallback((next: InventoryProduct[]) => {
    setProductsState(next);
    if (!loadedRef.current.products) {
      console.warn('[InventoryContext] Skipping save of inventory_products: initial fetch has not succeeded. Reload and try again once the server is reachable.');
      return;
    }
    saveInventoryProducts(next as unknown as import('../api/inventoryApi').InventoryProductRow[])
      .then((data) => setProductsState(data.map(mapApiProductToProduct)))
      .catch((err) => {
        void handleSaveFailure('inventory_products', err, refreshProducts);
      });
  }, [handleSaveFailure, refreshProducts]);

  const setUnits = useCallback((next: InventoryUnit[]) => {
    setUnitsState(next);
    if (!loadedRef.current.units) {
      console.warn('[InventoryContext] Skipping save of inventory_units: initial fetch has not succeeded. Reload and try again once the server is reachable.');
      return;
    }
    unitsSaveChainRef.current = unitsSaveChainRef.current
      .catch(() => { /* a failed save must not block the next one */ })
      .then(async () => {
        const data = await saveInventoryUnits(
          next as unknown as import('../api/inventoryApi').InventoryUnitRow[],
          { revision: unitsRevisionRef.current }
        );
        unitsRevisionRef.current = data.revision;
        setUnitsState(data.units.map(mapApiUnitToUnit));
      })
      .catch((err) => {
        void handleSaveFailure('inventory_units', err, refreshUnits);
      });
  }, [handleSaveFailure, refreshUnits]);

  const setTimEParts = useCallback((next: TimEInventoryRow[]) => {
    setTimEPartsState(next);
    if (!loadedRef.current.timEParts) {
      console.warn('[InventoryContext] Skipping save of tim_e_parts_inventory: initial fetch has not succeeded.');
      return;
    }
    saveTimEParts(next as unknown as import('../api/inventoryApi').TimEPartsRow[])
      .then((data) => setTimEPartsState(data.map(mapTimERow)))
      .catch((err) => {
        void handleSaveFailure('tim_e_parts_inventory', err, refreshTimEParts);
      });
  }, [handleSaveFailure, refreshTimEParts]);

  const setBimEParts = useCallback((next: BimEInventoryRow[]) => {
    setBimEPartsState(next);
    if (!loadedRef.current.bimEParts) {
      console.warn('[InventoryContext] Skipping save of bim_e_parts_inventory: initial fetch has not succeeded.');
      return;
    }
    saveBimEParts(next as unknown as import('../api/inventoryApi').BimEPartsRow[])
      .then((data) => setBimEPartsState(data.map(mapBimERow)))
      .catch((err) => {
        void handleSaveFailure('bim_e_parts_inventory', err, refreshBimEParts);
      });
  }, [handleSaveFailure, refreshBimEParts]);

  const setOperations = useCallback((next: OperationsInventoryRow[]) => {
    setOperationsState(next);
    if (!loadedRef.current.operations) {
      console.warn('[InventoryContext] Skipping save of operations_inventory: initial fetch has not succeeded.');
      return;
    }
    saveOperationsInventory(next as unknown as import('../api/inventoryApi').OperationsRow[])
      .then((data) => setOperationsState(data.map(mapOperationsRow)))
      .catch((err) => {
        console.error('[InventoryContext] Failed to save operations_inventory:', err);
      });
  }, []);

  const value: InventoryContextValue = {
    products,
    units,
    timEParts,
    bimEParts,
    operations,
    setProducts,
    setUnits,
    setTimEParts,
    setBimEParts,
    setOperations,
    refreshProducts,
    refreshUnits,
    refreshTimEParts,
    refreshBimEParts,
    refreshOperations,
    loading,
    loaded: {
      products: productsLoaded,
      units: unitsLoaded,
      timEParts: timEPartsLoaded,
      bimEParts: bimEPartsLoaded,
      operations: operationsLoaded,
    },
  };

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
}

export function useInventory(): InventoryContextValue {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error('useInventory must be used within InventoryProvider');
  return ctx;
}
